import { z } from 'zod'

import { analyticsEnvSchema } from './analytics/env-schema'

function isAbsoluteUrl(value: string) {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

const envSchema = z.object({
  /**
   * Either an absolute API origin or a root-relative prefix (e.g. `/api`) that
   * the dev server proxies — the upstream API sends no CORS headers, so the
   * browser must never call it cross-origin.
   */
  VITE_API_BASE_URL: z
    .string()
    .trim()
    .min(1)
    .refine((v) => !v.endsWith('/'), 'must not end with a slash')
    .refine(
      (v) => v.startsWith('/') || isAbsoluteUrl(v),
      'must be an absolute URL or a root-relative path such as /api',
    ),
  /**
   * OAuth 2.0 Web client ID from Google Cloud Console. Empty keeps the Google
   * button inert even if the feature flag is on.
   */
  VITE_GOOGLE_CLIENT_ID: z.string().trim().optional().default(''),
  /** Public reCAPTCHA v3 site key, never a secret key or a cached response token. */
  VITE_RECAPTCHA_SITE_KEY: z.string().trim().optional().default(''),
  /** Phoenix sender-auth API, isolated from the existing account/login gateway. */
  VITE_AUTH_API_BASE_URL: z
    .string()
    .trim()
    .default('/auth-api')
    .refine((v) => v.length > 0 && !v.endsWith('/'), 'must not be empty or end with a slash')
    .refine(
      (v) => v.startsWith('/') || isAbsoluteUrl(v),
      'must be an absolute URL or root-relative path',
    ),
  ...analyticsEnvSchema.shape,
  /**
   * `feature.jastiper_web_google_login` (WEBLP-2202 / WEBLP-2205).
   * When off, the Google button still renders but does not start OAuth.
   */
  VITE_FEATURE_JASTIPER_WEB_GOOGLE_LOGIN: z
    .string()
    .optional()
    .transform((v) => v?.trim().toLowerCase() === 'true' || v?.trim() === '1'),
  /** Customer service WhatsApp number (E.164, 62…, or 08…). Empty hides Hubungi CS. */
  VITE_CS_PHONE: z.string().trim().optional().default('6281119600999'),
  /**
   * Public Lion Parcel STT tracking site origin (no trailing slash).
   * Defaults: dev server → website-dev; prod build → lionparcel.com.
   * Set to `https://website-stg.thelionparcel.com` for staging deploys.
   */
  VITE_TRACKING_WEB_ORIGIN: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || !v.endsWith('/'), 'must not end with a slash')
    .refine((v) => !v || isAbsoluteUrl(v), 'must be an absolute URL'),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  // Fail loud at boot rather than with a confusing 404 on the first request.
  throw new Error(`Invalid environment variables:\n${z.prettifyError(parsed.error)}`)
}

export const env = parsed.data

/** `true` only on the Vite dev server (`pnpm dev`), not in `vite build` / preview. */
export const isDevelopment = import.meta.env.DEV

/** Google OAuth is live only when the flag is on *and* a web client ID is set. */
export const isGoogleLoginEnabled =
  env.VITE_FEATURE_JASTIPER_WEB_GOOGLE_LOGIN && env.VITE_GOOGLE_CLIENT_ID.length > 0
